import React from 'react'

import ICHRADepositAccountDetails from 'components/ICHRADepositAccountDetails'
import { useBreakpoint } from 'slab'
import {
  useGetMembersDepositAccountDetails,
  useGetMembersTransactionsBalanceAndBreakdowns,
} from 'utils/web_api'
import { type MembersBalance, type Money } from 'utils/web_api.schemas'

import { type MemberBalanceType } from '../types'
import MemberBalanceCard from './MemberBalanceCard/MemberBalanceCard'
import MemberBudgetCard from './MemberBudgetCard/MemberBudgetCard'

interface BalanceAndBudgetHeaderProps {
  planYear: number;
  isForTransactionsPage?: boolean;
  memberBalance?: MembersBalance;
}

interface WarningDates {
  start: Date;
  end: Date;
}

/**
 * Determines whether to display the member balance card based on premium coverage and QME status.
 *
 * Shows the balance when:
 * - There's any deduction_amount
 * - The monthly_allowance is less than the total_monthly_premiums (fallback, implies deduction)
 * - There's any balance_amount and QME is enabled
 * - monthly_allowance is greater than total_monthly_premiums and QME is enabled
 *
 * @param memberBalance - The member's balance and breakdowns
 * @returns True if the balance card should be displayed
 */
export const getShowMemberBalance = (memberBalance: MembersBalance) => {
  const hasDeduction =
    (memberBalance.deduction_amount &&
      memberBalance.deduction_amount.subunits > 0) ||
    memberBalance.monthly_allowance.subunits <
      memberBalance.total_monthly_premiums.subunits
  const hasBalance =
    memberBalance.balance_amount && memberBalance.balance_amount.subunits > 0
  const hasRemainder =
    memberBalance.monthly_allowance.subunits >
    memberBalance.total_monthly_premiums.subunits
  return (
    hasDeduction || (hasBalance && memberBalance.qme_enabled) || (hasRemainder && memberBalance.qme_enabled)
  )
}

/**
 * Calculates balance properties for display in the member balance card.
 *
 * Determines balance type based on:
 * - 'deduction' if there's a deduction amount (premiums exceed allowance)
 * - 'available' if it's the current year or balance can still be used
 * - 'unused' if it's a prior year and past the use-by date
 *
 * @param planYear - The plan year for this balance
 * @param warningDates - The warning dates for the balance
 * @param balanceAmount - The positive balance amount (optional)
 * @param deductionAmount - The deduction amount when premiums exceed allowance (optional)
 * @returns Object containing balance type, year status, amount, and usage constraints
 */
export const getBalanceProps = (
  planYear: number,
  warningDates: WarningDates,
  effectiveDate: Date,
  balanceAmount?: Money,
  deductionAmount?: Money
) => {
  const today = new Date()
  const thisYear = today.getFullYear()
  const canStillUseBalance = today <= warningDates.end

  const balanceType: MemberBalanceType = deductionAmount
    ? 'deduction'
    : planYear === thisYear || canStillUseBalance
      ? 'available'
      : 'unused'
  const subunits = deductionAmount
    ? deductionAmount.subunits
    : balanceAmount
      ? balanceAmount.subunits
      : 0

  return {
    balanceType,
    isPriorYear: planYear < thisYear,
    amount: Math.abs(subunits) / 100,
    canUse: {
      byDate: warningDates.end,
      forYear: planYear,
    },
    effectiveDate,
  }
}

export const BalanceAndBudgetHeader: React.FC<BalanceAndBudgetHeaderProps> = ({
  planYear,
  isForTransactionsPage = false,
  memberBalance: providedMemberBalance,
}) => {
  const { breakpointIsAtLeast } = useBreakpoint()

  const shouldFetchMemberBalance = providedMemberBalance === undefined

  const { data: hookMemberBalance } = useGetMembersTransactionsBalanceAndBreakdowns(
    {
      plan_year: planYear,
    }, {
      query: {
        refetchOnWindowFocus: false,
        enabled: shouldFetchMemberBalance
      }
    }
  )

  const memberBalance = shouldFetchMemberBalance ? hookMemberBalance : providedMemberBalance

  const { data: memberDepositAccount } = useGetMembersDepositAccountDetails({
    plan_year: planYear,
  }, {
    query: {
      enabled: isForTransactionsPage
    }
  })

  const shouldShowDepositAccountDetails =
    isForTransactionsPage &&
    memberDepositAccount?.account_number &&
    memberDepositAccount?.routing_number

  // Early return if no member balance
  if (!memberBalance) {
    return null
  }

  const showMemberBalance = getShowMemberBalance(memberBalance)
  const balanceProps = getBalanceProps(
    planYear,
    {
      start: new Date(memberBalance.deadline_warning_start_date),
      end: new Date(memberBalance.deadline_to_use_balance),
    },
    new Date(memberBalance.effective_date),
    memberBalance.balance_amount,
    memberBalance.deduction_amount
  )

  const memberBudgetCard = (
    <MemberBudgetCard
      budgetData={{
        monthlyAllowance: memberBalance.monthly_allowance.subunits / 100,
        totalMonthlyPremiums:
          memberBalance.total_monthly_premiums.subunits / 100,
        offThatchPremiums: memberBalance.off_thatch_premiums.subunits / 100,
        onThatchPremiums: memberBalance.on_thatch_premiums.subunits / 100,
        qmeEnabled: memberBalance.qme_enabled,
      }}
      isActive={!balanceProps.isPriorYear}
      numberOfPlans={memberBalance.number_of_plans}
      sideBySide={!showMemberBalance && !shouldShowDepositAccountDetails && breakpointIsAtLeast('lg')}
    />
  )

  const depositAccountDetails = shouldShowDepositAccountDetails
    ? (
      <ICHRADepositAccountDetails
        accountNumber={memberDepositAccount.account_number!}
        routingNumber={memberDepositAccount.routing_number!}
        memberSetsUpAutopay={
        memberDepositAccount.member_sets_up_autopay ?? false
      }
        widthClassOverride='w-full'
        heightClassOverride='h-full'
        roundBorderOverride='rounded-2xl'
        isActive={!balanceProps.isPriorYear}
      />
      )
    : null

  /* Special layout for lg breakpoint when both balance and deposit details are shown */
  const shouldUseSpecialLayout =
    shouldShowDepositAccountDetails &&
    showMemberBalance &&
    breakpointIsAtLeast('lg') &&
    !breakpointIsAtLeast('xl')

  if (shouldUseSpecialLayout) {
    return (
      <div data-testid='special-layout' className='flex flex-col w-full gap-4'>
        <div className='flex flex-row gap-6 items-stretch'>
          <div className='flex-grow'>
            <MemberBalanceCard {...balanceProps} />
          </div>
          <div className='flex-shrink-0'>
            {depositAccountDetails}
          </div>
        </div>
        <div>{memberBudgetCard}</div>
      </div>
    )
  }

  // Default layout
  const balanceWidth = shouldShowDepositAccountDetails ? '18.5rem' : '23.75rem'
  const classNames = breakpointIsAtLeast('lg')
    ? [
        'flex-row gap-6 items-stretch',
        `w-[15rem] xl:w-[${balanceWidth}] flex-shrink-0`,
        'flex-grow',
        'flex-shrink-0 max-w-[18.5rem]',
      ]
    : ['flex-col gap-4', 'min-w-[15rem]', 'min-w-[15rem]', 'min-w-[15rem]']

  return (
    <div data-testid='default-layout' className={`flex w-full ${classNames[0]}`}>
      {showMemberBalance && (
        <div className={classNames[1]}>
          <MemberBalanceCard {...balanceProps} />
        </div>
      )}
      <div className={classNames[2]}>{memberBudgetCard}</div>
      {shouldShowDepositAccountDetails && (
        <div className={classNames[3]}>{depositAccountDetails}</div>
      )}
    </div>
  )
}

export default BalanceAndBudgetHeader
